import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import { apiCall } from "../utils/apiService";
import { formatNumber } from "../utils/dashboardFormat";
import PageHero from "./child/PageHero";
import "./ListPage.css";
import "./CampaignLogLayer.css";
import "./MessageLogLayer.css";

const POLL_MS = 15000;

const parseContent = (val) => {
    if (!val) return "";
    try {
        const parsed = typeof val === "string" ? JSON.parse(val) : val;
        if (parsed && typeof parsed === "object") {
            return String(parsed.text || parsed.message || parsed.caption || JSON.stringify(parsed));
        }
        return String(val);
    } catch {
        return String(val);
    }
};

const parseErr = (val) => {
    if (!val) return "";
    try {
        const parsed = typeof val === "string" ? JSON.parse(val) : val;
        if (parsed && typeof parsed === "object") {
            return String(parsed.message || parsed.msg || parsed.err || JSON.stringify(parsed));
        }
        return String(val);
    } catch {
        return String(val);
    }
};

const statusTone = (status = "") => {
    const s = String(status).toUpperCase();
    if (s === "SUCCESS" || s === "SENT" || s === "COMPLETED") return "success";
    if (s === "QUEUE" || s === "PENDING" || s === "SCHEDULED") return "info";
    if (s === "PAUSED") return "secondary";
    return "danger";
};

const RUNNING_STATUSES = ["QUEUE", "PENDING", "PROCESSING", "RUNNING", "ACTIVE"];
const PAUSED_STATUSES = ["PAUSED", "STOPPED", "INACTIVE"];
const COMPLETED_STATUSES = ["COMPLETED", "DONE"];

const campaignBadge = (status = "") => {
    const raw = String(status || "").trim();
    const s = raw.toUpperCase();
    if (RUNNING_STATUSES.includes(s)) return { label: "Running", tone: "running", raw };
    if (PAUSED_STATUSES.includes(s)) return { label: "Paused", tone: "paused", raw };
    if (COMPLETED_STATUSES.includes(s)) return { label: "Completed", tone: "completed", raw };
    return { label: raw || "Unknown", tone: "error", raw };
};

const TruncateShowMore = ({ text, title, onView, maxLen = 48 }) => {
    const full = String(text || "").trim();
    if (!full) return <span className="log-reason-cell empty">—</span>;
    const needsMore = full.length > maxLen;
    return (
        <div className="log-reason-wrap">
            <span className="log-reason-cell" title={full}>
                {needsMore ? `${full.slice(0, maxLen)}…` : full}
            </span>
            {needsMore && (
                <button
                    type="button"
                    className="log-text-view-btn"
                    onClick={() => onView({ title, text: full })}
                >
                    Show more
                </button>
            )}
        </div>
    );
};

const PreviewModal = ({ title, text, onClose }) => {
    if (!text) return null;
    return (
        <div className="log-msg-modal-backdrop" onClick={onClose}>
            <div className="log-msg-modal" onClick={(e) => e.stopPropagation()}>
                <div className="log-msg-modal-header">
                    <h6>{title}</h6>
                    <button type="button" className="log-msg-modal-close" onClick={onClose}>
                        <Icon icon="mdi:close" width={18} />
                    </button>
                </div>
                <div className="log-msg-bubble">
                    <div className="log-msg-bubble-body" style={{ whiteSpace: "pre-wrap" }}>
                        {text}
                    </div>
                </div>
                <div className="log-msg-modal-actions">
                    <button
                        type="button"
                        className="log-msg-action"
                        onClick={async () => {
                            try {
                                await navigator.clipboard.writeText(text);
                            } catch {
                                /* ignore */
                            }
                        }}
                    >
                        <Icon icon="mdi:content-copy" width={16} />
                        Copy
                    </button>
                    <button type="button" className="log-msg-action primary" onClick={onClose}>
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

const CampaignLogLayer = () => {
    const { campaignId } = useParams();
    const [rows, setRows] = useState([]);
    const [successCount, setSuccessCount] = useState(0);
    const [otherCount, setOtherCount] = useState(0);
    const [campaignStatus, setCampaignStatus] = useState("");
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all"); // all | success | other | queue
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(20);
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(false);

    const fetchLogs = useCallback(
        async ({ silent = false } = {}) => {
            if (!campaignId) return;
            if (!silent) setLoading(true);
            try {
                const response = await apiCall({
                    url: `bulk_campaign/get_logs`,
                    method: "POST",
                    body: { campaign_id: campaignId },
                });
                const list = Array.isArray(response?.data) ? response.data : [];
                setRows(list);
                setSuccessCount(Number(response?.successArray) || 0);
                setOtherCount(Number(response?.otherArray) || 0);
                setCampaignStatus(response?.campaignStatus || "");
            } catch (error) {
                console.error("Error fetching campaign logs:", error);
                if (!silent) {
                    setRows([]);
                    setSuccessCount(0);
                    setOtherCount(0);
                }
            } finally {
                if (!silent) setLoading(false);
            }
        },
        [campaignId]
    );

    useEffect(() => {
        document.title = "Campaign Logs";
        fetchLogs();
        const id = setInterval(() => fetchLogs({ silent: true }), POLL_MS);
        const onFocus = () => fetchLogs({ silent: true });
        window.addEventListener("focus", onFocus);
        return () => {
            clearInterval(id);
            window.removeEventListener("focus", onFocus);
        };
    }, [fetchLogs]);

    const changeStatus = async (status) => {
        setBusy(true);
        try {
            const response = await apiCall({
                url: `bulk_campaign/change_status`,
                method: "POST",
                body: { campaign_id: campaignId, status },
            });
            if (response.success) {
                setCampaignStatus(status);
                fetchLogs({ silent: true });
            }
        } catch (error) {
            console.error("Error updating campaign status:", error);
        } finally {
            setBusy(false);
        }
    };

    const queueCount = useMemo(
        () => rows.filter((r) => String(r.status).toUpperCase() === "QUEUE").length,
        [rows]
    );

    const kpiTotal = successCount + otherCount;
    const progressPct =
        kpiTotal > 0 ? Math.round((successCount / kpiTotal) * 100) : 0;

    const filtered = useMemo(() => {
        let list = rows;
        const f = filter;
        if (f === "success") {
            list = list.filter((r) => String(r.status).toUpperCase() === "SUCCESS");
        } else if (f === "queue") {
            list = list.filter((r) => String(r.status).toUpperCase() === "QUEUE");
        } else if (f === "other") {
            list = list.filter((r) => {
                const s = String(r.status).toUpperCase();
                return s !== "SUCCESS" && s !== "QUEUE";
            });
        }
        if (search.trim()) {
            const q = search.toLowerCase();
            list = list.filter(
                (r) =>
                    String(r.mobile || "").toLowerCase().includes(q) ||
                    String(r.status || "").toLowerCase().includes(q) ||
                    String(r.sent_from || "").toLowerCase().includes(q) ||
                    parseContent(r.content).toLowerCase().includes(q) ||
                    parseErr(r.err).toLowerCase().includes(q)
            );
        }
        return list;
    }, [rows, filter, search]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / limit) || 1);
    const safePage = Math.min(page, totalPages);
    const pageRows = filtered.slice((safePage - 1) * limit, safePage * limit);
    const startIndex = filtered.length === 0 ? 0 : (safePage - 1) * limit + 1;
    const endIndex = Math.min(safePage * limit, filtered.length);

    const statusUpper = String(campaignStatus || "").toUpperCase();
    const badge = campaignBadge(campaignStatus);
    const isRunning = statusUpper === "QUEUE";
    const isCompletedDone =
        COMPLETED_STATUSES.includes(statusUpper) && queueCount === 0;
    const canStart = !isRunning && !isCompletedDone;
    const canStop = isRunning;

    return (
        <>
            {preview && (
                <PreviewModal
                    title={preview.title}
                    text={preview.text}
                    onClose={() => setPreview(null)}
                />
            )}

            <PageHero
                title="Campaign delivery"
                subtitle="Track each recipient for this broadcast — success, queue, and failures."
                badge={{ icon: "mdi:bullhorn-outline", label: "Campaign logs" }}
                action={
                    <Link to="/campaigns" className="btn btn-outline-success btn-sm">
                        <Icon icon="mdi:arrow-left" className="me-1" />
                        Back to campaigns
                    </Link>
                }
            />

            <div className="camp-log-control">
                <div className="camp-log-meta">
                    <span className="camp-log-pill" title={campaignId}>
                        <Icon icon="mdi:identifier" width={14} />
                        {String(campaignId).length > 22
                            ? `${String(campaignId).slice(0, 12)}…${String(campaignId).slice(-6)}`
                            : campaignId}
                    </span>
                    <span
                        className={`camp-log-pill tone-${badge.tone}`}
                        title={badge.raw || campaignStatus || "Unknown"}
                    >
                        <Icon icon="mdi:circle-medium" width={16} />
                        {badge.label}
                    </span>
                    <span className="camp-log-pill chip">
                        <Icon icon="mdi:check-circle-outline" width={14} />
                        Sent {formatNumber(successCount)}
                    </span>
                    <span className="camp-log-pill chip">
                        <Icon icon="mdi:alert-circle-outline" width={14} />
                        Other {formatNumber(otherCount)}
                    </span>
                    <span className="camp-log-pill chip">
                        <Icon icon="mdi:percent" width={14} />
                        Progress {progressPct}%
                    </span>
                </div>

                <div className="camp-log-actions">
                    <button
                        type="button"
                        className="camp-log-btn start"
                        disabled={busy || !canStart}
                        title={
                            isRunning
                                ? "Campaign is already running (QUEUE)"
                                : isCompletedDone
                                  ? "Campaign is completed with nothing left in queue"
                                  : "Start campaign (set status to QUEUE)"
                        }
                        onClick={() => changeStatus("QUEUE")}
                    >
                        <Icon icon="mdi:play" width={18} />
                        Start
                    </button>
                    <button
                        type="button"
                        className="camp-log-btn stop"
                        disabled={busy || !canStop}
                        title={
                            canStop
                                ? "Pause campaign (set status to PAUSED)"
                                : "Stop is only available while status is QUEUE"
                        }
                        onClick={() => changeStatus("PAUSED")}
                    >
                        <Icon icon="mdi:pause" width={18} />
                        Stop
                    </button>
                    <button
                        type="button"
                        className="camp-log-btn refresh"
                        disabled={busy || loading}
                        onClick={() => fetchLogs()}
                    >
                        <Icon icon="mdi:refresh" width={16} />
                        Refresh
                    </button>
                </div>
            </div>

            <div className="list-kpi-row camp-log-kpis">
                <div className="card list-kpi books">
                    <div className="list-kpi-body">
                        <div>
                            <p className="list-kpi-label">Progress</p>
                            <h6 className="list-kpi-value">{progressPct}%</h6>
                            <div className="camp-log-progress">
                                <span style={{ width: `${progressPct}%` }} />
                            </div>
                        </div>
                        <div className="list-kpi-icon">
                            <Icon icon="mdi:chart-donut" />
                        </div>
                    </div>
                </div>
                <div className="card list-kpi sent">
                    <div className="list-kpi-body">
                        <div>
                            <p className="list-kpi-label">Sent</p>
                            <h6 className="list-kpi-value">{formatNumber(successCount)}</h6>
                            <div className="list-kpi-actions">
                                <button type="button" className="list-kpi-btn view" onClick={() => { setFilter("success"); setPage(1); }}>
                                    View
                                </button>
                            </div>
                        </div>
                        <div className="list-kpi-icon">
                            <Icon icon="mdi:check-circle-outline" />
                        </div>
                    </div>
                </div>
                <div className="card list-kpi queue">
                    <div className="list-kpi-body">
                        <div>
                            <p className="list-kpi-label">In queue</p>
                            <h6 className="list-kpi-value">{formatNumber(queueCount)}</h6>
                            <div className="list-kpi-actions">
                                <button type="button" className="list-kpi-btn view" onClick={() => { setFilter("queue"); setPage(1); }}>
                                    View
                                </button>
                            </div>
                        </div>
                        <div className="list-kpi-icon">
                            <Icon icon="mdi:timer-sand" />
                        </div>
                    </div>
                </div>
                <div className="card list-kpi invalid">
                    <div className="list-kpi-body">
                        <div>
                            <p className="list-kpi-label">Pending / failed / other</p>
                            <h6 className="list-kpi-value">{formatNumber(otherCount)}</h6>
                            <div className="list-kpi-actions">
                                <button type="button" className="list-kpi-btn view" onClick={() => { setFilter("other"); setPage(1); }}>
                                    View
                                </button>
                            </div>
                        </div>
                        <div className="list-kpi-icon">
                            <Icon icon="mdi:alert-circle-outline" />
                        </div>
                    </div>
                </div>
            </div>

            <div className="card list-card">
                <div className="card-header">
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <div>
                            <h5 className="card-title mb-0">Recipient log</h5>
                            <p className="mb-0 text-secondary-light text-sm">
                                Auto-refreshes every 15s while this page is open
                            </p>
                        </div>
                        <div className="d-flex flex-wrap gap-2">
                            {[
                                { id: "all", label: "All" },
                                { id: "success", label: "Success" },
                                { id: "queue", label: "Queue" },
                                { id: "other", label: "Failed" },
                            ].map((item) => (
                                <button
                                    key={item.id}
                                    type="button"
                                    className={`btn btn-sm ${
                                        filter === item.id ? "btn-success" : "btn-outline-success"
                                    }`}
                                    onClick={() => {
                                        setFilter(item.id);
                                        setPage(1);
                                    }}
                                >
                                    {item.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="card-body">
                    <div className="d-flex justify-content-between flex-wrap gap-2 mb-3">
                        <div className="d-flex align-items-center gap-2">
                            <select
                                className="form-select radius-8"
                                style={{ width: 80 }}
                                value={limit}
                                onChange={(e) => {
                                    setLimit(Number(e.target.value));
                                    setPage(1);
                                }}
                            >
                                {[10, 20, 50, 100].map((n) => (
                                    <option key={n} value={n}>
                                        {n}
                                    </option>
                                ))}
                            </select>
                            <span className="text-secondary-light text-sm">entries per page</span>
                        </div>
                        <input
                            type="search"
                            className="form-control radius-8"
                            style={{ maxWidth: 280 }}
                            placeholder="Search mobile, status, error…"
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value);
                                setPage(1);
                            }}
                        />
                    </div>

                    {loading ? (
                        <div className="camp-log-empty">Loading campaign logs…</div>
                    ) : filtered.length === 0 ? (
                        <div className="camp-log-empty">
                            <Icon icon="mdi:clipboard-text-off-outline" />
                            <h6 className="mt-2 mb-1" style={{ color: "#111b21" }}>
                                No log entries
                            </h6>
                            <p className="mb-0">Try another filter or refresh after the campaign runs.</p>
                        </div>
                    ) : (
                        <>
                            <div className="camp-log-table-wrap">
                                <table className="camp-log-table">
                                    <thead>
                                        <tr>
                                            <th>ID</th>
                                            <th>Mobile</th>
                                            <th>Status</th>
                                            <th>Message</th>
                                            <th>Sent from</th>
                                            <th>Error / reason</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pageRows.map((row) => {
                                            const msg = parseContent(row.content);
                                            const err = parseErr(row.err);
                                            const tone = statusTone(row.status);
                                            return (
                                                <tr key={row.id}>
                                                    <td>
                                                        <span className="camp-log-id">#{row.id}</span>
                                                    </td>
                                                    <td className="fw-semibold">{row.mobile || "—"}</td>
                                                    <td>
                                                        <span className={`list-status ${tone}`}>
                                                            {row.status || "—"}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <TruncateShowMore
                                                            text={msg}
                                                            title="Message"
                                                            onView={setPreview}
                                                        />
                                                    </td>
                                                    <td>{row.sent_from || "—"}</td>
                                                    <td>
                                                        <TruncateShowMore
                                                            text={err}
                                                            title="Error / reason"
                                                            onView={setPreview}
                                                        />
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            <div className="pagination-controls d-flex flex-wrap align-items-center gap-2 justify-content-between mt-24">
                                <span className="pagination-meta">
                                    Showing {startIndex} to {endIndex} of {filtered.length} entries
                                </span>
                                <ul className="pagination d-flex flex-wrap align-items-center gap-2 mb-0">
                                    <li className="page-item">
                                        <button
                                            type="button"
                                            className="page-link"
                                            disabled={safePage <= 1}
                                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                                            aria-label="Previous page"
                                        >
                                            <Icon icon="ep:d-arrow-left" className="text-xl" />
                                        </button>
                                    </li>
                                    <li className="page-item">
                                        <button type="button" className="page-link is-active" aria-current="page">
                                            {safePage}
                                        </button>
                                    </li>
                                    <li className="page-item">
                                        <button
                                            type="button"
                                            className="page-link"
                                            disabled={safePage >= totalPages}
                                            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                            aria-label="Next page"
                                        >
                                            <Icon icon="ep:d-arrow-right" className="text-xl" />
                                        </button>
                                    </li>
                                </ul>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default CampaignLogLayer;
